<template>
  <div class="year-month-calendar">
    <div class="year-selector">
      <button
        class="arrow-button"
        @click="decrementYear"
        @keydown.left="decrementYear"
      >
        <i class="fas fa-chevron-left mr-2 secondary-icon" />
      </button>
      <span
        class="year-display"
        @click="clearMonth"
      >{{ selectedYear }}</span>
      <button
        class="arrow-button"
        @click="incrementYear"
        @keydown.right="incrementYear"
      >
        <i class="fas fa-chevron-right mr-2 secondary-icon" />
      </button>
    </div>

    <div class="months-list">
      <div
        v-for="(month, index) in localizedMonths"
        :key="index"
        class="month-item"
        :class="{
          'disabled': !allAvailable && !available.includes(`${month.id}-01`),
          'selected': !highlighted && isSelectedMonth(index),
          'highlighted': highlighted && highlighted.includes(`${month.id}-01`)
        }"
        @click="selectMonth(index, month.id)"
      >
        <template v-if="monthStatus[month.id]">
          <span
            v-if="monthStatus[month.id].new"
            class="status status-new"
          >+{{ monthStatus[month.id].new }}</span>
          <span
            v-if="monthStatus[month.id].cancel"
            class="status status-cancel"
          >-{{ Math.abs(monthStatus[month.id].cancel) }}</span>
          <span
            v-if="monthStatus[month.id].waiting"
            class="status status-waiting"
            :class="monthStatus[month.id].paid ? 'waiting-and-paid' : ''"
          >{{ monthStatus[month.id].waiting }}</span>
          <span
            v-if="monthStatus[month.id].paid"
            class="status status-paid"
          >{{ monthStatus[month.id].paid }}</span>
        </template>

        {{ month.label }}
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';

export default {
  props: {
    date: String,
    dayStatus: {
      type: Object,
      default: () => ({}),
    },
    highlighted: Array,
    initMonth: Number,
    allAvailable: Boolean,
    monthMode: Boolean,
    available: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      selectedYear: this.date ? moment(this.date).year() : moment().year(),
      selectedMonth: this.monthMode && this.date && (!this.highlighted || this.highlighted.length > 0)
        ? moment(this.date).month()
        : null,
    };
  },
  computed: {
    monthStatus() {
      return Object.keys(this.dayStatus)
        .reduce((acc, curr) => {
          acc[moment(curr).format('YYYY-MM')] = this.dayStatus[curr];
          return acc;
        }, {});
    },
    localizedMonths() {
      return Array.from({ length: 12 }, (_, i) => ({
        id: `${this.selectedYear}-${(i + 1).toString().padStart(2, '0')}`,
        label: moment().month(i).format('MMMM'),
      }));
    },
  },
  methods: {
    getMonthRange(monthIndex) {
      const month = `${this.selectedYear}-${(monthIndex + 1).toString().padStart(2, '0')}-01`;

      return {
        from: month,
        to: moment(month).add(1, 'month').format('YYYY-MM-DD'),
      };
    },
    emitYearRange() {
      if (this.monthMode && this.selectedMonth !== null) {
        this.$emit('update:dates', this.getMonthRange(this.selectedMonth));
        return;
      }

      this.$emit('update:dates', {
        from: `${this.selectedYear}-01-01`,
        to: `${this.selectedYear + 1}-01-01`,
      });
    },
    decrementYear() {
      this.selectedYear -= 1;

      if (!this.monthMode) {
        this.selectedMonth = null;
      }

      this.emitYearRange();
    },
    incrementYear() {
      this.selectedYear += 1;

      if (!this.monthMode) {
        this.selectedMonth = null;
      }

      this.emitYearRange();
    },
    clearMonth() {
      this.$emit('update:selected', null);

      this.selectedMonth = null;

      if (this.monthMode) {
        this.emitYearRange();
      }
    },
    selectMonth(index, monthId) {
      if (!this.allAvailable && !this.available.includes(`${monthId}-01`)) {
        return;
      }

      const month = `${this.selectedYear}-${(index + 1).toString().padStart(2, '0')}-01`;

      if (this.monthMode) {
        if (this.selectedMonth !== index) {
          this.selectedMonth = index;
          this.$emit('update:selected', month);
          this.$emit('update:dates', {
            from: month,
            to: moment(month).add(1, 'month').format('YYYY-MM-DD'),
          });
        }
        return;
      }

      if (this.selectedMonth === index) {
        this.$emit('update:selected', null);
        this.selectedMonth = null;
      } else {
        this.$emit('update:selected', month);
        this.selectedMonth = index;
      }

      if (this.highlighted) {
        const from = `${this.selectedYear}-01-01`;
        const to = `${this.selectedYear + 1}-01-01`;

        const list = this.highlighted.includes(month)
          ? this.highlighted.filter((x) => x !== month)
          : this.highlighted.concat([month]);

        this.$emit('highlighted', { list, from, to });
      }
    },
    isSelectedMonth(index) {
      return this.selectedMonth === index;
    },
  },
  created() {
    if (this.monthMode && this.date && (!this.highlighted || this.highlighted.length > 0)) {
      const initialMonth = moment(this.date).startOf('month').format('YYYY-MM-DD');

      this.selectedYear = moment(initialMonth).year();
      this.selectedMonth = moment(initialMonth).month();
      this.$emit('update:selected', initialMonth);
      this.$emit('update:dates', {
        from: initialMonth,
        to: moment(initialMonth).add(1, 'month').format('YYYY-MM-DD'),
      });
      return;
    }

    if (this.initMonth) {
      this.selectMonth(this.initMonth - 1);
    }
    this.$emit('update:dates', {
      from: `${this.selectedYear}-01-01`,
      to: `${this.selectedYear + 1}-01-01`,
    });
  },
};
</script>

<style lang="scss" scoped>
.year-month-calendar {
  max-width: 300px;
  margin: 0 auto;
}

.status {
  position: absolute;
  top: -2px;
  right: -2px;
  width: 18px;
  height: 18px;
  line-height: 18px;
  font-size: 9px;
  font-weight: 500;
  color: white;
  border-radius: 50%;
}

.status-new {
  background-color: $blue;
  top: auto;
  bottom: -2px;
}

.status-cancel {
  background-color: #aaa;
  top: auto;
  right: auto;
  left: 1px;
  bottom: 1px;
}
.status-waiting {
  background-color: $orange;

  &.waiting-and-paid {
    right: 15px;
  }
}
.status-paid {
  background-color: $lightblue;
}

.year-selector {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
  gap: 10px;
}

.year-display {
  font-size: 18px;
  font-weight: 500;
  min-width: 80px;
  text-align: center;
  cursor: pointer;

  &:hover {
    background-color: #f5f5f5;
  }
}

.arrow-button {
  background: none;
  padding: 8px 12px;
  cursor: pointer;
  font-size: 16px;
  border: none;
  transition: all 0.2s ease;
}

.arrow-button:hover {
  background-color: #f5f5f5;
}

.arrow-button:focus {
  outline: none;
  border-color: $blue;
}

.months-list {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
}

.month-item {
  padding: 8px 8px;
  text-align: center;
  border: 2px solid #ddd;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 400;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;

  &:hover {
    background-color: #f5f5f5;
  }

  &.highlighted {
    border-color: $blue;
  }

  &.disabled {
    opacity: 0.5;

    &:hover {
      background-color: #fff;
    }
  }
}

.month-item.selected {
  background-color: #007bff;
  color: white;
  border-color: #0056b3;
}
</style>
